import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Save, TestTube2, CheckCircle, XCircle, Mail, History, RotateCcw, Trash2 } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatDistanceToNow } from "date-fns";
import { ChatGPTMonitoring } from "@/components/chatgpt-monitoring";
import { IgnoredEmailRecovery } from "@/components/ignored-email-recovery";

interface SystemSetting {
  id: number;
  settingKey: string;
  settingValue: string;
  settingType: string;
}

interface EmailMatchingSettings {
  forwardingEmail: string;
  blacklistKeywords: string[];
  whitelistSenders: string[];
  whitelistProjects: string[];
  chatgptEnabled: boolean;
  chatgptModel: string;
  chatgptPrompt: string;
}

interface PromptHistory {
  id: number;
  promptText: string;
  createdAt: string;
  createdBy?: number;
}

export function EmailMatchingSettings() {
  const { toast } = useToast();
  
  // Form state
  const [forwardingEmail, setForwardingEmail] = useState("");
  const [blacklistKeywords, setBlacklistKeywords] = useState("");
  const [whitelistSenders, setWhitelistSenders] = useState("");
  const [whitelistProjects, setWhitelistProjects] = useState("");
  const [chatgptEnabled, setChatgptEnabled] = useState(false);
  // Empty until the stored settings arrive; the server owns the defaults, so
  // the UI never carries its own (quickly outdated) model name.
  const [chatgptModel, setChatgptModel] = useState("");
  const [summaryModel, setSummaryModel] = useState("");
  const [transcriptionModel, setTranscriptionModel] = useState("");
  const [chatgptPrompt, setChatgptPrompt] = useState("");

  // Fetch settings
  const { data: settings, isLoading } = useQuery<SystemSetting[]>({
    queryKey: ['/api/settings/email-matching'],
  });

  // Fetch prompt history
  const { data: promptHistory } = useQuery<PromptHistory[]>({
    queryKey: ['/api/chatgpt/prompt-history'],
    enabled: chatgptEnabled,
  });

  // Fetch pricing information for model selection
  const { data: pricingModels = [] } = useQuery<Array<{
    id: string;
    name: string;
    description: string;
    inputPrice: number;
    outputPrice: number;
    estimatedCostPer1000Emails: number;
  }>>({
    queryKey: ['/api/chatgpt/pricing'],
  });

  // Speech-to-text models for call transcription (priced per minute)
  const { data: transcriptionModels = [] } = useQuery<Array<{
    id: string;
    name: string;
    description: string;
    recommended: boolean;
    pricePerMinute: number;
  }>>({
    queryKey: ['/api/chatgpt/transcription-models'],
  });

  // Populate form when settings are loaded
  useEffect(() => {
    if (settings && settings.length > 0) {
      settings.forEach(setting => {
        const value = setting.settingValue || "";
        
        switch (setting.settingKey) {
          case 'email_forwarding_address':
            setForwardingEmail(value);
            break;
          case 'email_blacklist_keywords':
            try {
              const keywords = JSON.parse(value);
              setBlacklistKeywords(Array.isArray(keywords) ? keywords.join(', ') : '');
            } catch {
              setBlacklistKeywords('');
            }
            break;
          case 'email_whitelist_senders':
            try {
              const senders = JSON.parse(value);
              setWhitelistSenders(Array.isArray(senders) ? senders.join(', ') : '');
            } catch {
              setWhitelistSenders('');
            }
            break;
          case 'email_whitelist_projects':
            try {
              const projects = JSON.parse(value);
              setWhitelistProjects(Array.isArray(projects) ? projects.join(', ') : '');
            } catch {
              setWhitelistProjects('');
            }
            break;
          case 'email_chatgpt_enabled':
            setChatgptEnabled(value === 'true');
            break;
          case 'email_chatgpt_model':
            setChatgptModel(value);
            break;
          case 'ai_summary_model':
            setSummaryModel(value);
            break;
          case 'ai_transcription_model':
            setTranscriptionModel(value);
            break;
          case 'email_chatgpt_prompt':
            setChatgptPrompt(value);
            break;
        }
      });
    }
  }, [settings]);

  // Save settings mutation
  const saveSettingsMutation = useMutation({
    mutationFn: async () => {
      const settingsToSave: Record<string, string> = {
        'email_forwarding_address': forwardingEmail.trim(),
        'email_blacklist_keywords': JSON.stringify(
          blacklistKeywords.split(',').map(k => k.trim()).filter(k => k)
        ),
        'email_whitelist_senders': JSON.stringify(
          whitelistSenders.split(',').map(s => s.trim()).filter(s => s)
        ),
        'email_whitelist_projects': JSON.stringify(
          whitelistProjects.split(',').map(p => p.trim()).filter(p => p)
        ),
        'email_chatgpt_enabled': chatgptEnabled.toString(),
        'email_chatgpt_prompt': chatgptPrompt.trim(),
      };

      // Only send model keys once a value is actually loaded/picked, so an
      // unloaded form can never overwrite the stored model with a blank.
      if (chatgptModel.trim()) settingsToSave['email_chatgpt_model'] = chatgptModel.trim();
      if (summaryModel.trim()) settingsToSave['ai_summary_model'] = summaryModel.trim();
      if (transcriptionModel.trim()) settingsToSave['ai_transcription_model'] = transcriptionModel.trim();

      const response = await apiRequest('PATCH', '/api/settings/email-matching', settingsToSave);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/settings/email-matching'] });
      queryClient.invalidateQueries({ queryKey: ['/api/chatgpt/prompt-history'] });
      toast({
        title: "Settings Saved",
        description: "Email matching settings have been updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save email matching settings",
        variant: "destructive",
      });
    },
  });

  // Test ChatGPT connection
  const testChatGPTMutation = useMutation({
    mutationFn: async () => {
      // This would call a test endpoint - placeholder for now
      const response = await apiRequest('POST', '/api/chatgpt/test');
      return await response.json();
    },
    onSuccess: () => {
      toast({
        title: "Connection Successful",
        description: "ChatGPT API is working correctly",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Connection Failed",
        description: error.message || "Unable to connect to ChatGPT API",
        variant: "destructive",
      });
    },
  });

  // Delete prompt history entry
  const deletePromptMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest('DELETE', `/api/chatgpt/prompt-history/${id}`);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/chatgpt/prompt-history'] });
      toast({
        title: "Prompt Deleted",
        description: "Prompt history entry has been deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Delete Failed",
        description: error.message || "Failed to delete prompt history",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">Email Matching & Validation</h3>
      </div>

      {/* Information Alert */}
      <Alert className="bg-blue-900/20 border-blue-600">
        <Mail className="h-4 w-4 text-blue-400" />
        <AlertDescription className="text-blue-300 text-sm">
          Configure how incoming emails are validated, filtered, and assigned to projects automatically.
        </AlertDescription>
      </Alert>

      {/* Forwarding Email */}
      <div className="space-y-2">
        <Label htmlFor="forwarding-email" className="text-gray-300">
          Forwarding Email Address
        </Label>
        <Input
          id="forwarding-email"
          type="email"
          value={forwardingEmail}
          onChange={(e) => setForwardingEmail(e.target.value)}
          placeholder="admin@example.com"
          className="bg-gray-800 border-gray-600 text-white placeholder-gray-400"
          data-testid="input-forwarding-email"
        />
        <p className="text-xs text-gray-400">
          Email address to forward unmatched or flagged emails for manual review
        </p>
      </div>

      <IgnoredEmailRecovery />

      {/* Blacklist Keywords */}
      <div className="space-y-2">
        <Label htmlFor="blacklist-keywords" className="text-gray-300">
          Blacklist Keywords
        </Label>
        <Textarea
          id="blacklist-keywords"
          value={blacklistKeywords}
          onChange={(e) => setBlacklistKeywords(e.target.value)}
          placeholder="spam, advertisement, unsubscribe"
          className="bg-gray-800 border-gray-600 text-white placeholder-gray-400 min-h-[80px]"
          data-testid="textarea-blacklist-keywords"
        />
        <p className="text-xs text-gray-400">
          Comma-separated keywords. Emails containing these will be flagged or rejected
        </p>
      </div>

      {/* Whitelist Senders */}
      <div className="space-y-2">
        <Label htmlFor="whitelist-senders" className="text-gray-300">
          Whitelist Email Addresses
        </Label>
        <Textarea
          id="whitelist-senders"
          value={whitelistSenders}
          onChange={(e) => setWhitelistSenders(e.target.value)}
          placeholder="client@company.com, partner@business.de"
          className="bg-gray-800 border-gray-600 text-white placeholder-gray-400 min-h-[80px]"
          data-testid="textarea-whitelist-senders"
        />
        <p className="text-xs text-gray-400">
          Comma-separated email addresses. Always allow emails from these senders
        </p>
      </div>

      {/* Whitelist Projects */}
      <div className="space-y-2">
        <Label htmlFor="whitelist-projects" className="text-gray-300">
          Whitelist Project IDs
        </Label>
        <Textarea
          id="whitelist-projects"
          value={whitelistProjects}
          onChange={(e) => setWhitelistProjects(e.target.value)}
          placeholder="25-WE01, 25-BB02"
          className="bg-gray-800 border-gray-600 text-white placeholder-gray-400 min-h-[60px]"
          data-testid="textarea-whitelist-projects"
        />
        <p className="text-xs text-gray-400">
          Comma-separated project IDs. Automatically assign emails with these project IDs in subject
        </p>
      </div>

      {/* ChatGPT Settings Section */}
      <div className="border-t border-gray-600 pt-6 space-y-4">
        <h4 className="text-md font-medium text-white">ChatGPT Integration</h4>
        
        {/* ChatGPT Enabled */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="chatgpt-enabled" className="text-gray-300">
              Enable ChatGPT Analysis
            </Label>
            <p className="text-xs text-gray-400">
              Use AI to analyze and categorize incoming emails
            </p>
          </div>
          <Switch
            id="chatgpt-enabled"
            checked={chatgptEnabled}
            onCheckedChange={setChatgptEnabled}
            data-testid="switch-chatgpt-enabled"
          />
        </div>

        {/* ChatGPT Model */}
        <div className="space-y-2">
          <Label htmlFor="chatgpt-model" className="text-gray-300">
            ChatGPT Model
          </Label>
          <Select value={chatgptModel} onValueChange={setChatgptModel}>
            <SelectTrigger className="bg-gray-800 border-gray-600 text-white" data-testid="select-chatgpt-model">
              <SelectValue placeholder="Select a model" />
            </SelectTrigger>
            <SelectContent>
              {pricingModels.length > 0 ? (
                pricingModels.map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    <div className="flex items-center justify-between w-full">
                      <span>{model.name}</span>
                      <span className="text-xs text-muted-foreground ml-4">
                        ${model.inputPrice}/${model.outputPrice} per 1M tokens
                      </span>
                    </div>
                  </SelectItem>
                ))
              ) : (
                chatgptModel ? <SelectItem value={chatgptModel}>{chatgptModel}</SelectItem> : null
              )}
            </SelectContent>
          </Select>
          {pricingModels.length > 0 && (
            <p className="text-xs text-gray-400">
              Selected model: <span className="text-purple-400">{chatgptModel}</span> · 
              Est. cost for 1000 emails: <span className="text-green-400">
                ${pricingModels.find(m => m.id === chatgptModel)?.estimatedCostPer1000Emails.toFixed(4) || '?'}
              </span>
            </p>
          )}
          {!pricingModels.length && (
            <p className="text-xs text-gray-400">
              OpenAI model to use for email analysis
            </p>
          )}
        </div>

        {/* Summary / keyword model (call pulses, project search keywords) */}
        <div className="space-y-2">
          <Label htmlFor="summary-model" className="text-gray-300">
            Model for call summaries &amp; search keywords
          </Label>
          <Select value={summaryModel} onValueChange={setSummaryModel}>
            <SelectTrigger className="bg-gray-800 border-gray-600 text-white" data-testid="select-summary-model">
              <SelectValue placeholder="Select a model" />
            </SelectTrigger>
            <SelectContent>
              {pricingModels.length > 0 ? (
                pricingModels.map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    <div className="flex items-center justify-between w-full">
                      <span>{model.name}</span>
                      <span className="text-xs text-muted-foreground ml-4">
                        ${model.inputPrice}/${model.outputPrice} per 1M tokens
                      </span>
                    </div>
                  </SelectItem>
                ))
              ) : (
                summaryModel ? <SelectItem value={summaryModel}>{summaryModel}</SelectItem> : null
              )}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-400">
            Used when a call recording is turned into a pulse summary and for the
            keywords behind project search.
          </p>
        </div>

        {/* Transcription model (speech-to-text for call recordings) */}
        <div className="space-y-2">
          <Label htmlFor="transcription-model" className="text-gray-300">
            Model for call transcription (speech-to-text)
          </Label>
          <Select value={transcriptionModel} onValueChange={setTranscriptionModel}>
            <SelectTrigger className="bg-gray-800 border-gray-600 text-white" data-testid="select-transcription-model">
              <SelectValue placeholder="Select a model" />
            </SelectTrigger>
            <SelectContent>
              {transcriptionModels.length > 0 ? (
                transcriptionModels.map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    <div className="flex items-center justify-between w-full">
                      <span>{model.name}{model.recommended ? ' (recommended)' : ''}</span>
                      <span className="text-xs text-muted-foreground ml-4">
                        ${model.pricePerMinute.toFixed(4)}/min
                      </span>
                    </div>
                  </SelectItem>
                ))
              ) : (
                transcriptionModel ? <SelectItem value={transcriptionModel}>{transcriptionModel}</SelectItem> : null
              )}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-400">
            Used to turn call recordings into text before the summary is written.
            Whisper and the Diarize model keep per-sentence timestamps for screen-snapshot
            placement; the newer models trade that for better German transcripts.
          </p>
        </div>

        {/* ChatGPT Prompt */}
        <div className="space-y-2">
          <Label htmlFor="chatgpt-prompt" className="text-gray-300">
            System Prompt
          </Label>
          <Textarea
            id="chatgpt-prompt"
            value={chatgptPrompt}
            onChange={(e) => setChatgptPrompt(e.target.value)}
            placeholder="Analyze this email and determine if it's related to project management..."
            className="bg-gray-800 border-gray-600 text-white placeholder-gray-400 min-h-[120px]"
            data-testid="textarea-chatgpt-prompt"
          />
          <p className="text-xs text-gray-400">
            Instructions for ChatGPT on how to analyze emails. Include what to look for and how to classify.
          </p>
        </div>

        {/* Prompt Version History */}
        {promptHistory && promptHistory.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <History className="w-4 h-4" />
              <span>Prompt History (Last {promptHistory.length})</span>
            </div>
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg max-h-60 overflow-y-auto">
              <div className="divide-y divide-gray-700">
                {promptHistory.map((historyItem) => (
                  <div 
                    key={historyItem.id} 
                    className="p-3 hover:bg-gray-700/30 transition-colors group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-400 mb-1">
                          {formatDistanceToNow(new Date(historyItem.createdAt), { addSuffix: true })}
                        </div>
                        <pre className="text-xs text-gray-300 whitespace-pre-wrap break-words font-mono bg-gray-900/50 p-2 rounded border border-gray-700 max-h-32 overflow-y-auto">
                          {historyItem.promptText}
                        </pre>
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <Button
                          onClick={() => setChatgptPrompt(historyItem.promptText)}
                          size="sm"
                          variant="ghost"
                          className="text-blue-400 hover:text-blue-300 hover:bg-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`button-restore-prompt-${historyItem.id}`}
                        >
                          <RotateCcw className="h-3 w-3 mr-1" />
                          Restore
                        </Button>
                        <Button
                          onClick={() => deletePromptMutation.mutate(historyItem.id)}
                          size="sm"
                          variant="ghost"
                          disabled={deletePromptMutation.isPending}
                          className="text-red-400 hover:text-red-300 hover:bg-red-600/10 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`button-delete-prompt-${historyItem.id}`}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Test ChatGPT Connection */}
        <Button
          onClick={() => testChatGPTMutation.mutate()}
          disabled={!chatgptEnabled || testChatGPTMutation.isPending}
          variant="outline"
          className="border-purple-600 text-purple-400 hover:bg-purple-600/10"
          data-testid="button-test-chatgpt"
        >
          {testChatGPTMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Testing...
            </>
          ) : (
            <>
              <TestTube2 className="h-4 w-4 mr-2" />
              Test Connection
            </>
          )}
        </Button>

        {/* ChatGPT Monitoring - API Call History and Cost Dashboard */}
        {chatgptEnabled && (
          <div className="border-t border-gray-600 pt-6 mt-6">
            <ChatGPTMonitoring />
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex gap-3 pt-4 border-t border-gray-600">
        <Button
          onClick={() => saveSettingsMutation.mutate()}
          disabled={saveSettingsMutation.isPending}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="button-save-settings"
        >
          {saveSettingsMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      {/* Information */}
      <div className="text-sm text-gray-400 p-4 bg-gray-800/50 border border-gray-700 rounded-lg">
        <div className="font-medium text-gray-300 mb-2">Email Matching System</div>
        <ul className="space-y-1 text-xs">
          <li>• Incoming emails are validated against blacklist keywords</li>
          <li>• Whitelisted senders bypass all filtering rules</li>
          <li>• Project IDs in email subjects trigger automatic assignment</li>
          <li>• ChatGPT can analyze email content and suggest categorization</li>
          <li>• Unmatched emails are forwarded to the configured admin address</li>
          <li>• All settings take effect immediately after saving</li>
        </ul>
      </div>
    </div>
  );
}
